import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { API_BASE } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import { Product } from '../../types';
import { validateFile } from '../../constants/fileUpload';
import './AdminProducts.css';

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  imageUrl: string;
  croppedImageUrl: string;
  aspectRatio: string;
  size: string;
  material: string;
  finish: string;
  isCustomPrint: boolean;
}

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null; name?: string }>({ show: false, id: null });
  const [deleting, setDeleting] = useState<boolean>(false);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    croppedImageUrl: '',
    aspectRatio: '',
    size: '',
    material: '',
    finish: '',
    isCustomPrint: false
  });
  const [uploading, setUploading] = useState<boolean>(false);
  const { success, error: showError } = useToast();

  const fetchProducts = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Product[]>('/admin/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: keyof ProductFormData): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file using centralized validation
    const validation = validateFile(file);
    if (!validation.isValid) {
      showError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear the input
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<{ imageUrl: string }>('/admin/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({ ...prev, [field]: response.data.imageUrl }));
    } catch (error) {
      showError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (!formData.name.trim() || !formData.price) {
        showError('Please fill in the required fields.');
        return;
      }

      setSaving(true);
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        isCustomPrint: formData.isCustomPrint === true || (typeof formData.isCustomPrint === 'string' && formData.isCustomPrint === 'true')
      };

      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, productData);
        success('Product updated');
      } else {
        await api.post('/admin/products', productData);
        success('Product created');
      }

      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      showError('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product: Product): void => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      imageUrl: product.imageUrl || '',
      croppedImageUrl: product.croppedImageUrl || '',
      aspectRatio: product.aspectRatio || '',
      size: product.size || '',
      material: product.material || '',
      finish: product.finish || '',
      isCustomPrint: product.isCustomPrint || false
    });
    setShowForm(true);
  };

  const handleDelete = async (productId: number): Promise<void> => {
    setDeleteConfirm({ show: true, id: productId, name: products.find(p => p.id === productId)?.name });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      setDeleting(true);
      await api.delete(`/admin/products/${deleteConfirm.id}`);
      success('Product deleted');
      fetchProducts();
      setDeleteConfirm({ show: false, id: null });
    } catch (error) {
      showError('Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: '',
      description: '',
      price: '',
      imageUrl: '',
      croppedImageUrl: '',
      aspectRatio: '',
      size: '',
      material: '',
      finish: '',
      isCustomPrint: false
    });
  };

  const handleCancel = (): void => {
    setShowForm(false);
    setEditingProduct(null);
    resetForm();
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="admin-products">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1 className="page-title">Product Management</h1>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i> Add Product
          </button>
        </div>

        {showForm && (
          <div className="product-form-container">
            <form onSubmit={handleSubmit} className="product-form">
              <h2>{editingProduct ? 'Edit Product' : 'Create Product'}</h2>
              
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.isCustomPrint.toString()}
                    onChange={(e) => setFormData({ ...formData, isCustomPrint: e.target.value === 'true' })}
                  >
                    <option value="false">Art Reproduction</option>
                    <option value="true">Custom Print</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <div className="file-upload-group">
                  <input
                    type="text"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="Or upload image below"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'imageUrl')}
                    disabled={uploading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Cropped Image URL</label>
                <div className="file-upload-group">
                  <input
                    type="text"
                    value={formData.croppedImageUrl}
                    onChange={(e) => setFormData({ ...formData, croppedImageUrl: e.target.value })}
                    placeholder="Or upload image below"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'croppedImageUrl')}
                    disabled={uploading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Aspect Ratio</label>
                  <input
                    type="text"
                    value={formData.aspectRatio}
                    onChange={(e) => setFormData({ ...formData, aspectRatio: e.target.value })}
                    placeholder="e.g., 1, 0.8, 16/9"
                  />
                </div>

                <div className="form-group">
                  <label>Size</label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    placeholder="e.g., 8x10, 11x14"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Material</label>
                  <input
                    type="text"
                    value={formData.material}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    placeholder="e.g., Glossy, Canvas"
                  />
                </div>

                <div className="form-group">
                  <label>Finish</label>
                  <input
                    type="text"
                    value={formData.finish}
                    onChange={(e) => setFormData({ ...formData, finish: e.target.value })}
                    placeholder="e.g., No Frame, Floater Frame"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={uploading || saving}>
                  {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                  {saving ? 'Saving...' : (editingProduct ? 'Update' : 'Create')}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          onConfirm={confirmDelete}
          loading={deleting}
          itemName={deleteConfirm.name}
        />

        <div className="products-grid">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                <img
                  src={product.croppedImageUrl || product.imageUrl || '/placeholder-image.jpg'}
                  alt={product.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== product.imageUrl && product.imageUrl) {
                      target.src = product.imageUrl.startsWith('http') 
                        ? product.imageUrl 
                        : `${API_BASE}${product.imageUrl}`;
                    } else {
                      target.src = '/placeholder-image.jpg';
                    }
                  }}
                />
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-price">${product.price.toFixed(2)}</p>
                {product.size && <p>Size: {product.size}</p>}
                {product.material && <p>Material: {product.material}</p>}
                <p className="product-type">
                  {product.isCustomPrint ? 'Custom Print' : 'Art Reproduction'}
                </p>
                <div className="product-actions">
                  <button
                    onClick={() => handleEdit(product)}
                    className="btn btn-primary btn-small"
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="btn btn-danger btn-small"
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
